import React from 'react';
import InstallScreen from './InstallScreen';
import { useSession } from '../lib/session';
import { useFlag } from '../lib/flags';
import { isStandalone } from '../lib/pwa';
import {
  emptyInstallState,
  markDismissed,
  markInstalled,
  markShown,
  normaliseState,
  shouldShowInstall,
  startSession,
} from '../lib/installPrompt';

// A UI preference, not a record: the rules allow a localStorage cache for this.
// TODO(P7): mirror into member_prefs.install_state via prefs_set so a member who
// installs on their phone is not asked again on their laptop.
const KEY = 'ihsanone_install_state';

function read() {
  try {
    return normaliseState(JSON.parse(window.localStorage.getItem(KEY)));
  } catch {
    return emptyInstallState();
  }
}

function write(state) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* private mode — we simply ask again next time */
  }
}

/**
 * Decides whether to show the install screen, and remembers the answer.
 * The timing rules are in lib/installPrompt.js, deliberately kept out of React.
 */
export default function InstallPromptHost() {
  const session = useSession();
  const enabled = useFlag('install_prompt');

  // Count the session once per app start, before anything reads the state.
  const [state, setState] = React.useState(() => {
    const next = startSession(read());
    write(next);
    return next;
  });

  // Latched deliberately. Recording "we asked" immediately makes the condition
  // false again, so deriving visibility straight from the state would show the
  // screen for a single frame and then tear it away.
  const [activeReason, setActiveReason] = React.useState(null);

  const update = React.useCallback((fn) => {
    setState((current) => {
      const next = fn(current);
      write(next);
      return next;
    });
  }, []);

  React.useEffect(() => {
    if (activeReason || !enabled) return;
    const reason = shouldShowInstall(state, {
      standalone: isStandalone(),
      signedIn: Boolean(session),
    });
    if (!reason) return;
    setActiveReason(reason);
    update((current) => markShown(current, reason));
  }, [activeReason, enabled, state, session, update]);

  if (!activeReason) return null;

  const close = (fn) => {
    update(fn);
    setActiveReason(null);
  };

  return (
    <InstallScreen
      onDismiss={() => close(markDismissed)}
      onInstalled={() => close(markInstalled)}
    />
  );
}
